/**
 * W3C selector resolver.
 *
 * Resolution order:
 *   1. TextPositionSelector — verify text at [start, end] matches exact. Fast path.
 *   2. TextQuoteSelector (exact) — indexOf scan. Handles minor position drift.
 *   3. TextQuoteSelector (prefix anchor) — find prefix, take exact.length chars after.
 *      Handles one-character mutations inside the exact span when prefix is stable.
 *   4. Orphan — return null; caller surfaces the annotation in the orphan rail.
 */

import type { Selector } from './selectors';

/** Convert char offsets [start, end] into a live DOM Range inside `container`. */
export function offsetsToRange(container: HTMLElement, start: number, end: number): Range | null {
  const range = document.createRange();
  let charCount = 0;
  let startSet = false;

  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
  let n: Node | null;

  while ((n = walker.nextNode())) {
    const text = n as Text;
    const len = text.data.length;
    const nodeEnd = charCount + len;

    if (!startSet && start >= charCount && start <= nodeEnd) {
      range.setStart(text, start - charCount);
      startSet = true;
    }

    if (startSet && end >= charCount && end <= nodeEnd) {
      range.setEnd(text, end - charCount);
      return range;
    }

    charCount = nodeEnd;
  }

  return null;
}

/**
 * Resolve a stored `Selector` back to a DOM Range.
 *
 * Returns `null` when the anchor cannot be found — the caller should mark the
 * annotation as orphaned and show it in the orphan rail rather than hiding it.
 */
export function resolveSelector(container: HTMLElement, selector: Selector): Range | null {
  const text = container.textContent ?? '';
  const { position, quote } = selector;
  const { start, end } = position;
  const { exact, prefix } = quote;

  // 1. Position-first: text at stored offsets matches exact — perfect case.
  if (start >= 0 && end > start && end <= text.length) {
    if (text.slice(start, end) === exact) {
      return offsetsToRange(container, start, end);
    }
  }

  // 2. Exact substring search — handles position drift without content change.
  if (exact.length > 0) {
    const idx = text.indexOf(exact);
    if (idx !== -1) {
      return offsetsToRange(container, idx, idx + exact.length);
    }
  }

  // 3. Prefix anchor — handles one-character mutation inside the exact span.
  //    Find where prefix ends; the selection starts there with the same length.
  if (prefix.length >= 4) {
    const prefixIdx = text.indexOf(prefix);
    if (prefixIdx !== -1) {
      const newStart = prefixIdx + prefix.length;
      const newEnd = newStart + exact.length;
      if (newEnd <= text.length) {
        return offsetsToRange(container, newStart, newEnd);
      }
    }
  }

  // 4. Orphaned.
  return null;
}
